import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/context/SettingsContext";

const ease = [0.16, 1, 0.3, 1];
const HERO_IMG = "/hero-hue.webp?v=6";
const chipPos = [
  { top: "17%", left: "12%", depth: 32, delay: 0.9 },
  { top: "46%", right: "9%", depth: -26, delay: 1.05 },
  { bottom: "18%", left: "18%", depth: 22, delay: 1.2 },
];

export default function HeroInteractive() {
  const { t } = useTranslation();
  const { seasonalActive: summer } = useSettings();
  const words = t("hero.heading").split(" ");
  const chips = [
    { label: t("hero.chip1"), ...chipPos[0] },
    { label: t("hero.chip2"), ...chipPos[1] },
    { label: t("hero.chip3"), ...chipPos[2] },
  ];
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 18 });
  const sy = useSpring(my, { stiffness: 120, damping: 18 });

  // Disable the scroll parallax on phones (≤767px) — keep it on desktop/tablet.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const mobileRef = useRef(isMobile);
  useEffect(() => { mobileRef.current = isMobile; }, [isMobile]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const rotateY = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const glowX = useTransform(sx, [-0.5, 0.5], ["35%", "65%"]);
  const glowY = useTransform(sy, [-0.5, 0.5], ["35%", "65%"]);
  const spotlight = useTransform(
    [glowX, glowY],
    ([x, y]) =>
      `radial-gradient(600px circle at ${x} ${y}, ${summer ? "rgba(247,148,30,0.16)" : "rgba(219,150,13,0.10)"}, transparent 60%)`
  );

  // per-chip parallax (hooks must live at top level, not inside .map)
  const c0x = useTransform(sx, [-0.5, 0.5], [-chips[0].depth, chips[0].depth]);
  const c0y = useTransform(sy, [-0.5, 0.5], [-chips[0].depth, chips[0].depth]);
  const c1x = useTransform(sx, [-0.5, 0.5], [-chips[1].depth, chips[1].depth]);
  const c1y = useTransform(sy, [-0.5, 0.5], [-chips[1].depth, chips[1].depth]);
  const c2x = useTransform(sx, [-0.5, 0.5], [-chips[2].depth, chips[2].depth]);
  const c2y = useTransform(sy, [-0.5, 0.5], [-chips[2].depth, chips[2].depth]);
  const chipMV = [{ x: c0x, y: c0y }, { x: c1x, y: c1y }, { x: c2x, y: c2y }];

  // scroll-driven 3D depth: subtle & premium on desktop; fully disabled on mobile.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const smoothScroll = useSpring(scrollYProgress, { stiffness: 90, damping: 24, mass: 0.4 });
  const scrollRotateX = useTransform(smoothScroll, (v) => (mobileRef.current ? 0 : v * 8));
  const scrollScale = useTransform(smoothScroll, (v) => (mobileRef.current ? 1 : 1 - v * 0.06));
  const scrollLift = useTransform(smoothScroll, (v) => (mobileRef.current ? 0 : v * 46));
  const scrollFade = useTransform(smoothScroll, (v) =>
    mobileRef.current ? 1 : 1 - Math.min(v / 0.85, 1) * 0.65
  );
  const ringParallax = useTransform(smoothScroll, (v) => (mobileRef.current ? 0 : v * 38));
  // combine live mouse tilt with the scroll-driven recline (a single rotateX can't hold both)
  const combinedRotateX = useTransform([rotateX, scrollRotateX], ([m, s]) => m + s);

  // whole-hero light parallax: layers drift at slightly different speeds on scroll for gentle depth.
  const heroParallax = useTransform(smoothScroll, (v) => (mobileRef.current ? 0 : v * 60));
  const bgParallax = useTransform(smoothScroll, (v) => (mobileRef.current ? 0 : v * -40));

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <section
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 md:pt-20"
    >
      {/* moving spotlight */}
      <motion.div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{ background: spotlight }}
      />
      {/* summer sunrise wash */}
      {summer && (
        <div
          data-testid="hero-summer-glow"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(115% 80% at 82% -12%, rgba(255,196,89,0.34), rgba(255,122,89,0.12) 42%, transparent 68%)",
          }}
        />
      )}
      {/* dotted texture */}
      <motion.div
        style={{ y: bgParallax }}
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(${summer ? "#E4C489" : "#BDBDBD"} 1px, transparent 1px)`,
            backgroundSize: "26px 26px",
            maskImage: "radial-gradient(ellipse 70% 60% at 60% 40%, black, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 60% 40%, black, transparent 75%)",
          }}
        />
      </motion.div>

      <motion.div
        style={{ y: heroParallax }}
        className="relative mx-auto max-w-[1400px] w-full px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-6 items-center"
      >
        {/* Copy */}
        <div className="order-2 md:order-1 text-center md:text-left">
          <motion.span
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            {t("hero.badge")}
          </motion.span>

          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter text-zinc-900 leading-[1.02]">
            {words.map((w, i) => (
              <motion.span
                key={i}
                className="inline-block mr-[0.25em]"
                initial={{ y: 28 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 + i * 0.1, ease }}
              >
                {w}
              </motion.span>
            ))}
          </h1>

          <p className="mt-6 text-lg md:text-xl text-zinc-500 max-w-md mx-auto md:mx-0">
            {t("hero.subtext")}
          </p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75, ease }}
            className="mt-9 flex flex-wrap items-center justify-center md:justify-start gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group inline-flex items-center gap-2 bg-zinc-900 text-white pl-6 pr-5 py-3.5 rounded-full text-sm font-medium pill-fx pill-fx-dark"
            >
              {t("hero.shopBtn")}
              <span className="grid place-items-center w-6 h-6 rounded-full bg-white/15 group-hover:translate-x-0.5 transition-transform">
                <ArrowRight strokeWidth={2} className="pill-fx-arrow w-3.5 h-3.5" />
              </span>
            </Link>
            <Link
              to="/shop/philips-hue"
              className="group inline-flex items-center text-gold hover:text-gold-dark text-sm md:text-base font-medium transition-colors"
            >
              {t("hero.explore")}
              <ChevronRight strokeWidth={2} className="w-4 h-4 mt-0.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>

        {/* Interactive product stage */}
        <motion.div
          className="order-1 md:order-2 relative h-[42vh] md:h-[70vh] [perspective:1200px]"
          style={{ y: scrollLift, scale: scrollScale, opacity: scrollFade }}
        >
          {/* gold halo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease }}
            className="absolute inset-0 m-auto w-[70%] aspect-square rounded-full blur-3xl"
            style={{ background: summer ? "radial-gradient(circle, rgba(255,138,60,0.32), transparent 65%)" : "radial-gradient(circle, rgba(219,150,13,0.22), transparent 65%)" }}
          />
          {/* rotating ring (CSS spin keeps the main thread idle) */}
          <motion.div className="absolute inset-0" style={{ y: ringParallax }}>
            <div
              className="absolute inset-0 m-auto w-[78%] aspect-square rounded-full border border-zinc-300/70 hero-spin"
              style={{ maskImage: "linear-gradient(black, transparent 70%)" }}
            />
          </motion.div>

          <motion.div
            className="absolute inset-0"
            style={{ rotateX: combinedRotateX, rotateY, transformStyle: "preserve-3d" }}
          >
            {/* transparent product, floating freely (float loop runs in CSS) */}
            <div className="absolute inset-0 hero-float" style={{ transformStyle: "preserve-3d" }}>
              <motion.img
                src={HERO_IMG}
                srcSet={`/hero-hue-700.webp?v=6 700w, ${HERO_IMG} 1200w`}
                sizes="(min-width: 768px) 45vw, 90vw"
                alt="Flush Wall Mount for Philips Hue Motion Sensor"
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 m-auto w-[94%] h-[94%] object-contain"
                style={{ translateZ: 50 }}
                initial={{ y: 50, scale: 0.92 }}
                animate={{ y: 0, scale: 1 }}
                transition={{ duration: 0.9, delay: 0.25, ease }}
              />
            </div>

            {/* floating spec chips */}
            {chips.map((c, i) => (
              <motion.div
                key={i}
                className="absolute z-10 rounded-full bg-white/70 backdrop-blur-md border border-white/60 shadow-sm px-3.5 py-2 text-xs font-medium text-zinc-800 whitespace-nowrap"
                style={{
                  top: c.top, left: c.left, right: c.right, bottom: c.bottom,
                  x: chipMV[i].x,
                  y: chipMV[i].y,
                  translateZ: 90,
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: c.delay, ease }}
              >
                <span className="mr-1.5 inline-block w-1.5 h-1.5 rounded-full bg-gold align-middle" />
                {c.label}
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400"
      >
        {t("hero.scroll")}
        <span className="w-5 h-8 rounded-full border border-zinc-300 flex justify-center pt-1.5">
          <span className="w-1 h-1.5 rounded-full bg-zinc-500 hero-scroll-dot" />
        </span>
      </motion.div>
    </section>
  );
}
